import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import type { IframePoolItem } from '../../../modules/api';
import { Skeleton } from '../../../modules/design-system';
import { groupByTier, UNTIERED_TITLE, type TierSection } from './groupByTier';
import { HitCard, type HitCardSize } from './HitCard';
import { HitSheet } from './HitSheet';

type Props = {
  items: IframePoolItem[];
  loading: boolean;
};

const SKELETON_COUNT = 6;

const GRIDS: Record<HitCardSize, string> = {
  large: 'grid-cols-4',
  small: 'grid-cols-6',
};

/** The top tier gets the big cards; cards with no tier at all the small ones. */
function sizeOf(section: TierSection, index: number): HitCardSize {
  return index === 0 && section.title !== UNTIERED_TITLE ? 'large' : 'small';
}

/**
 * Desktop Potential Hits of the glass storefront (Figma 19074:253777): a
 * grid per tier ("Grail", "Chase", …) under the tier's name. The phone
 * shows them all in one grid in the "What's inside?" sheet.
 */
export function PotentialHitsByTier({ items, loading }: Props) {
  const [selected, setSelected] = useState<IframePoolItem | null>(null);

  if (loading && items.length === 0) {
    return (
      <div className={`grid gap-3 ${GRIDS.small}`}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <Skeleton key={i} className="aspect-[185/284] w-full rounded-[16px]" />
        ))}
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <section aria-label="Potential hits" className="flex flex-col gap-8">
      {groupByTier(items).map((section, index) => {
        const size = sizeOf(section, index);
        return (
          <div key={section.title} className="flex flex-col gap-4">
            <h2 className="text-[22px] font-semibold leading-[30px] text-white">{section.title}</h2>
            <div className={`grid gap-3 ${GRIDS[size]}`}>
              {section.items.map((item) => (
                <HitCard key={item.id} item={item} size={size} onClick={setSelected} />
              ))}
            </div>
          </div>
        );
      })}
      <AnimatePresence>
        {selected && <HitSheet item={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
